/**
 * Legal Agreement / E-Signature module — Phase A (the tenant-facing agreement, not the
 * platform-owned MSA/DPA templates). See docs/adr/0015-agreement-esignature-architecture.md.
 *
 * An `Agreement` is one organisation's signed-order-form instance: company legal details, a
 * point-in-time pricing/plan snapshot, and the DocuSign envelope's lifecycle. It snapshots the
 * `msaTemplateId`/`dpaTemplateId` of the active platform templates at creation time, so a future
 * template version is free to be published without touching an already-created agreement.
 *
 * `pricingSnapshot`/`planCode` are deliberately plain strings — this module produces a signed
 * agreement and a plan-code placeholder for a future billing module to consume, nothing more.
 *
 * Phase A ships create/list/get and a `send` route that only validates preconditions (Phase B
 * wires the actual DocuSign envelope call). No `sign`/`reject`/webhook routes yet — those, and the
 * MSA+SOF+DPA single-envelope ceremony, are Phase B.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  emailSchema, errorBodySchema, idempotencyKeySchema, instantSchema, orgIdSchema,
  paginatedSchema, paginationQuerySchema, uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const agreementStatusSchema = z.enum(['draft', 'sent', 'viewed', 'signed', 'rejected', 'expired']);

export const agreementSignerStatusSchema = z.enum(['pending', 'sent', 'viewed', 'signed', 'declined']);

export const agreementSignerSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  email: emailSchema,
  title: z.string().nullable(),
  status: agreementSignerStatusSchema,
  createdAt: instantSchema,
});
export type AgreementSigner = z.infer<typeof agreementSignerSchema>;

export const agreementSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  status: agreementStatusSchema,
  version: z.number().int().positive(),
  companyLegalName: z.string(),
  gstin: z.string().nullable(),
  cin: z.string().nullable(),
  pan: z.string().nullable(),
  userCount: z.number().int().positive(),
  subscriptionPeriod: z.string(),
  pricingSnapshot: z.string().nullable(),
  planCode: z.string().nullable(),
  sofNumber: z.string().nullable().optional(),
  msaTemplateId: uuidSchema,
  dpaTemplateId: uuidSchema,
  docusignEnvelopeId: z.string().nullable(),
  sentAt: instantSchema.nullable(),
  viewedAt: instantSchema.nullable(),
  signedAt: instantSchema.nullable(),
  signedDocumentUrl: z.string().url().nullable(),
  createdAt: instantSchema,
  signers: z.array(agreementSignerSchema),
});
export type Agreement = z.infer<typeof agreementSchema>;

/** Staff-typed commercial terms for the SOF. Plain strings — this is not billing (ADR 0015). */
export const agreementPlanSchema = z.enum(['Essential HR & Payroll', 'Professional', 'Total Workforce Platform']);
export const agreementBillingCycleSchema = z.enum(['Monthly', '6-monthly', 'Annual']);
const commercialText = z.string().trim().max(2_000).optional();
export const agreementCommercialSchema = z.object({
  plan: agreementPlanSchema.optional(),
  addOns: commercialText,
  activeEmployees: z.string().trim().max(20).optional(),
  billingCycle: agreementBillingCycleSchema.optional(),
  subscriptionStartDate: z.string().trim().max(40).optional(),
  baseFeeCalc: commercialText,
  baseFeeAmount: commercialText,
  additionalCalc: commercialText,
  additionalAmount: commercialText,
  addOnsCalc: commercialText,
  addOnsAmount: commercialText,
  discountPct: commercialText,
  discountAmount: commercialText,
  totalCycle: commercialText,
  gstAmount: commercialText,
  totalPayable: commercialText,
  affiliates: commercialText,
  specialTerms: commercialText,
});
export type AgreementCommercial = z.infer<typeof agreementCommercialSchema>;

/**
 * Company fields (`companyLegalName`/`gstin`/`cin`/`pan`/`signer`) are OPTIONAL overrides: by
 * default they come from the org's completed `CompanyLegalProfile` (company-profile.contract.ts).
 */
export const createAgreementBodySchema = z.object({
  companyLegalName: z.string().trim().min(1).max(300).optional(),
  gstin: z.string().trim().min(1).max(20).optional(),
  cin: z.string().trim().min(1).max(30).optional(),
  pan: z.string().trim().min(1).max(20).optional(),
  userCount: z.number().int().positive(),
  subscriptionPeriod: z.string().trim().min(1).max(100).optional(),
  pricingSnapshot: z.string().trim().max(2_000).optional(),
  planCode: z.string().trim().max(100).optional(),
  signer: z.object({
    name: z.string().trim().min(1).max(200),
    email: emailSchema,
    title: z.string().trim().max(200).optional(),
  }).optional(),
  commercial: agreementCommercialSchema.optional(),
});

export const listAgreementsQuerySchema = paginationQuerySchema;

export const agreementContract = c.router({
  create: {
    method: 'POST',
    path: '/agreements',
    body: createAgreementBodySchema,
    responses: {
      201: agreementSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema,
      412: errorBodySchema, 422: errorBodySchema,
    },
    summary: 'Create a draft agreement, snapshotting the currently-active MSA/DPA templates',
    metadata: routeMeta('agreement:manage'),
  },
  list: {
    method: 'GET',
    path: '/agreements',
    query: listAgreementsQuerySchema,
    responses: { 200: paginatedSchema(agreementSchema), 401: errorBodySchema, 403: errorBodySchema },
    summary: "List the org's agreements",
    metadata: routeMeta('agreement:read'),
  },
  get: {
    method: 'GET',
    path: '/agreements/:agreementId',
    responses: { 200: agreementSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
    metadata: routeMeta('agreement:read'),
  },
  send: {
    method: 'POST',
    path: '/agreements/:agreementId/send',
    body: z.object({}),
    headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
    responses: {
      200: agreementSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema,
      412: errorBodySchema, 422: errorBodySchema,
    },
    summary: 'Send the agreement for signature via DocuSign — Phase B; Phase A validates preconditions only',
    metadata: routeMeta('agreement:manage'),
  },
});
